import React, { useEffect, useState } from 'react';
import DataTable_AgGrid from './DataTable_AgGrid'; 
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import './Forms.css';
import RtfMemo from './Helpers';  // Importeer je RtfMemo component
import { convertYoutubeURLtoEmbed, getVideoUrl, buildTreeData } from './Helpers';
import { useAuth } from './AuthContext';
import api from './axios';
import 'rc-tree/assets/index.css';

// Import rc-tree en CSS
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';

const ICON_SPACE = 24; // px, ruimte voor boom-icoon + indentatie


function FormPage({ companyPortalModuleId, recordId, onClose, onOpenForm, showBack }) {
  const { user, companyId, logout } = useAuth();
  const userEmail = user?.email;

  const [formConfig, setFormConfig] = useState(null);
  const [formData, setFormData] = useState(null);
  const [subTablesData, setSubTablesData] = useState({});
  const [userPermission, setUserPermission] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingMain, setIsLoadingMain] = useState(true);
  const [isLoadingSub, setIsLoadingSub] = useState(false);

  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [showPopupMessage, setShowPopupMessage] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  // Hulp: check of tab toegestaan is
  const isTabAllowed = (layoutItem) => {
    if (!layoutItem || !layoutItem.label) {
      // geen label → toon de tab
      return true;
    }
    const modPerm = userPermission.find(
      p => Number(p.company_portal_module_id) === Number(companyPortalModuleId)
    );
    if (!modPerm?.layout?.length) return true;
    const found = modPerm.layout.find(l => l && l.label === layoutItem.label);
    return found?.permission !== false;
  };

  // Reset state bij record-wijziging
  useEffect(() => {
    setFormConfig(null);
    setFormData(null);
    setSubTablesData({});
    setActiveTab(0);
    setIsLoadingMain(true);
  }, [recordId]);

  // 1) Laad module-form-config
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post('/api/get_company_portal_module', {
          moduleId: companyPortalModuleId
        });
        if (!data.module_form_json_definition) {
          throw new Error('Module configuration not found');
        }
        setFormConfig(JSON.parse(data.module_form_json_definition));
      } catch (err) {
        console.error(err);
        setError('Error loading module config: ' + err.message);
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    })();
  }, [companyPortalModuleId]);

  // 2) Laad hoofdrecord
  useEffect(() => {
    if (!formConfig) return;
    (async () => {
      try {
        const payload = {
          companyId,
          email: userEmail,
          query: formConfig.query,
          params: [recordId]
        };
        const { data: rows } = await api.post('/api/iAGateway_get_query', payload);
        setFormData(rows[0] || {});
      } catch (err) {
        console.error(err);
        setError('Error loading form data: ' + err.message);
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      } finally {
        setIsLoadingMain(false);
      }
    })();
  }, [formConfig, recordId, companyId, userEmail]);

  // 3) Lazy-load subtables voor actieve tab
  useEffect(() => {
    if (!formConfig || !formData) return;
    // filter én guard nulls
    const layouts = (formConfig.layout || [])
      .filter(item =>
        item
        && (item.type === 'subtable' || item.type === 'tree' || item.type === 'tabpage-layout')
        && !item.hide
        && isTabAllowed(item)
      );
    if (!layouts.length) return;
    // reset tab als out-of-range
    if (activeTab >= layouts.length) setActiveTab(0);

    const layout = layouts[activeTab];
    if (
      (layout.type === 'subtable' && layout.subtableConfig && !subTablesData[activeTab]) ||
      (layout.type === 'tree' && layout.treeConfig && !subTablesData[activeTab])
    ) {
      (async () => {
        setIsLoadingSub(true);
        try {
          const q = layout.type === 'tree'
            ? layout.treeConfig.query
            : layout.subtableConfig.query;
          const payload = {
            companyId,
            email: userEmail,
            query: q,
            params: [recordId]
          };
          const { data } = await api.post('/api/iAGateway_get_query', payload);
          setSubTablesData(prev => ({ ...prev, [activeTab]: data }));
        } catch (err) {
          console.error(err);
          setError('Error loading subtable/tree: ' + err.message);
          setShowError(true);
          setTimeout(() => setShowError(false), 3000);
        } finally {
          setIsLoadingSub(false);
        }
      })();
    }
  }, [activeTab, formConfig, formData, recordId, companyId, userEmail, userPermission]);

  // 4) Laad per-user permissies
  useEffect(() => {
    if (!userEmail) return;
    (async () => {
      try {
        // bepaal endpoint op basis van app-type
        const appType = localStorage.getItem('iA-App');
        const endpoint =
          appType === 'Employee Portal'
            ? '/api/get_ep_user_permission'
            : appType === 'Customer Portal'
              ? '/api/get_cp_user_permission'
              : appType === 'Supplier Portal'
                ? '/api/get_sp_user_permission'
                : null;
        const { data } = await api.post(endpoint, { companyId, email: userEmail });
        const raw = data.json_permission || data.users?.[0]?.json_permission || '[]';
        let perms = JSON.parse(raw);
        if (typeof perms === 'string') perms = JSON.parse(perms);
        setUserPermission(Array.isArray(perms) ? perms : []);
      } catch (err) {
        console.error(err);
        setError('Error loading permissions: ' + err.message);
        setShowError(true);
        setTimeout(() => setShowError(false), 3000);
      }
    })();
  }, [userEmail, companyId]);

  // Helper: formatteer veldwaarden (datum, decimalen, boolean, etc.)
  const formatValue = (fld, value) => {
    if (fld.type === 'date') {
      if (!value) return '';
      const date = new Date(value);
      if (fld.style === 'dd-mm-yyyy') {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return date.toLocaleDateString();
    }
    if (fld.type === 'decimal') {
      const numberValue = Number(value);
      if (isNaN(numberValue)) return value;
      let decimals = 2; 
      let prefix = '';
      if (fld.style) {
        if (fld.style.includes('€') || fld.style.includes('$') || fld.style.includes('£')) {
          prefix = fld.style;
          decimals = 2;
        } else {
          const parts = fld.style.split(',');
          if (parts.length > 1) {
            decimals = parts[1].length;
          }
        }
      }
      return prefix + numberValue.toFixed(decimals);
    }
    if (fld.type === 'boolean') {
      return value ? '✅' : '☐';
    }
    return value !== undefined && value !== null ? value : '';
  };

  // Functie voor het renderen van de bovenste widgets (key-value paren)
  const renderWidgetColumn = (widget, className) => {
    // Filter alleen de velden die zichtbaar moeten zijn (hide is false of niet gedefinieerd)
    const visibleFields = widget.fields.filter(fld => !fld.hide);
  
    return (
      <div
        className={className}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          padding: '20px',
          flex: 1
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '7px' }}>
          {visibleFields.map((fld, idx) => {
            let value;
            if (fld.valueGetter) {
              try {
                // Vervang "data.FIELDNAME" door een veilige getter (aangenomen dat formData beschikbaar is)
                const safeGetter = fld.valueGetter.replace(/data\.(\w+)/g, '(data.$1 || \'\')');
                const fn = new Function('data', `return ${safeGetter};`);
                value = fn(formData);
              } catch (e) {
                console.error('Error evaluating valueGetter:', e);
                value = '';
              }
            } else {
              value = formData[fld.field];
            }
            return (
              <React.Fragment key={idx}>
                <div className={`${className}_key_text`}>{fld.label}</div>
                <div className={`${className}_value_text`} style={{ fontWeight: 'bold' }}>
                  {formatValue(fld, value)}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };
  

  if (isLoadingMain || !formConfig || !formData) {
    return (
      <div className="loader-wrapper-new">
        <div className="loader-new"></div>
      </div>
    );
  }
  if (showPopupMessage) {
    return <div className="popup-message">{message}</div>;
  }
  if (showErrorPopup) {
    return <div className="popup">{error}</div>;
  }

  // Bovenste widgets
  const widgetLeft = formConfig?.layout?.find(item => item.type === 'widget' && item.fields && item.fields.length > 0);
  const widgetRight = formConfig?.layout?.find((item, idx) => item.type === 'widget' && idx === 1);

  // Filter voor tab-layouts: 'subtable', 'tree', 'tabpage-layout'
  const tabLayouts = (formConfig.layout || []).filter(
    item => item &&
      (item.type === 'subtable' || item.type === 'tree' || item.type === 'tabpage-layout') &&
      !item.hide
  );
  const permittedTabLayouts = tabLayouts.filter(layout => isTabAllowed(layout));
  const hasTabLayouts = permittedTabLayouts.length > 0;

  // Renders de inhoud van de tabs, inclusief 'tree'
  const renderTabs = () => {
    if (hasTabLayouts) {
      const visibleTabLayouts = permittedTabLayouts.filter(layout => !layout.hide);
      if (visibleTabLayouts.length === 0) return null;
      if (activeTab >= visibleTabLayouts.length) setActiveTab(0);

      return (
        <div className="widgetTabs" style={{ marginTop: '0px', marginBottom: '20px' }}>
          <Tabs
            onSelect={(index) => setActiveTab(index)}
            style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            <TabList className="tabList">
              {visibleTabLayouts.map((layout, idx) => (
                <Tab key={idx} className="tabButton" selectedClassName="tabButtonActive">
                  {layout.label || `Tab ${idx + 1}`}
                </Tab>
              ))}
            </TabList>
            {visibleTabLayouts.map((layout, idx) => (
              <TabPanel
                key={idx}
                className="tabPanel"
                style={{ display: 'flex', flexDirection: 'row', height: '54vh', maxHeight: '54vh' }}
              >
                {activeTab === idx && layout.type === 'subtable' && layout.subtableConfig && (
                  <div style={{ 
                    height: layout.subtableConfig.height || '52vh', 
                    maxHeight: layout.subtableConfig.maxheight || '52vh',
                    paddingTop: '15px', 
                    width: '100%',
                    position: 'relative'
                  }}>
                    {subTablesData[idx] ? (
                      <DataTable_AgGrid
                        initialData={subTablesData[idx]}
                        columns={layout.subtableConfig.columns}
                        isEditable={true}
                        config={layout.subtableConfig}
                        moduleId={companyPortalModuleId}
                        recordId={recordId}
                        onOpenForm={onOpenForm}
                      />
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%' 
                      }}>
                        <div>
                          <div className="spinner" style={{ margin: '0 auto' }}></div>
                          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '1.1em', fontWeight: 'bold' }}>
                            Loading subtable data...
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === idx && layout.type === 'tree' && layout.treeConfig && (
                  <div
                    style={{
                      // 1) De totale hoogte (header + boom)
                      height: layout.treeConfig.height || '52vh',
                      maxHeight: layout.treeConfig.maxheight || '52vh',
                      paddingTop: '15px',
                      width: '100%',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {subTablesData[idx] ? (
                      (() => {
                        const flatData = subTablesData[idx];
                        const columns = layout.treeConfig.columns || [];
                        const pkField = layout.treeConfig.primaryKey;   // dynamische primaryKey
                        const parentField = 'PARENT_ASSEMBLY_ID';        // vast uit de CTE
                        const childField = 'CHILD_ASSEMBLY_ID';          // idem

                        // (1) Bouw een map parentAssemblyId → array van detail‐records
                        const assemblyMap = {};
                        flatData.forEach(detail => {
                          const parentId = detail[parentField];
                          if (!assemblyMap[parentId]) {
                            assemblyMap[parentId] = [];
                          }
                          assemblyMap[parentId].push(detail);
                        });

                        // (2) Recursieve functie om nodes te bouwen voor een gegeven assemblyId
                        function buildNodesForAssembly(assemblyId) {
                          const details = assemblyMap[assemblyId] || [];
                          return details.map(detail => {
                            // Verzamel niet‐lege kolomwaarden en concateneer met '|'
                            const values = columns
                              .map(col => {
                                const v = detail[col.field];
                                return v != null && String(v).trim() !== '' ? String(v).trim() : null;
                              })
                              .filter(v => v !== null);
                            const joined = values.join(' | ');

                            // Title‐JSX: we laten rc-tree zelf de indent/switcher/icon renderen,
                            // en plaatsen rechts daarvan onze samengevoegde string
                            const titleJsx = (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontSize: '0.85rem',
                                    fontFamily: 'Segoe UI, sans-serif',
                                  }}
                                >
                                  {joined}
                                </div>
                              </div>
                            );

                            // Recursief kinderen toevoegen (indien aanwezig)
                            const children =
                              detail[childField] != null
                                ? buildNodesForAssembly(detail[childField])
                                : [];

                            return {
                              key: String(detail[pkField]),
                              title: titleJsx,
                              data: detail,
                              children,
                            };
                          });
                        }

                        // (3) Header: samengevoegde kolomnamen met ' | '
                        const headerValues = columns
                          .map(col => (col.headerName || '').trim())
                          .filter(name => name !== '');
                        const headerJoined = headerValues.join(' | ');

                        // (4) Bouw de daadwerkelijke treeData vanaf de root‐assembly
                        const treeData = buildNodesForAssembly(recordId);

                        //  Hoeveel pixels is de header in totaal (inclusief padding/border)?
                        const HEADER_HEIGHT_PX = 32;  

                        // (5) Styling voor de header (met “placeholder” voor indent/switcher/icon)
                        const headerStyle = {
                          display: 'flex',
                          alignItems: 'center',
                          backgroundColor: '#f5f5f5',
                          padding: '6px 12px',
                          borderBottom: '1px solid #ddd',
                          fontWeight: 'bold',
                          height: `${HEADER_HEIGHT_PX}px`,
                          boxSizing: 'border-box',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                          flexShrink: 0,
                        };

                        // (6) Bepaal de hoogte voor het Tree‐gedeelte
                        const containerHeight = layout.treeConfig.height || '52vh';
                        const treeContainerHeight = `calc(${containerHeight} - ${HEADER_HEIGHT_PX}px)`;

                        return (
                          <>
                            {/* Header‐rij: we simuleren hier EXACT de rc-tree indent/switcher/icon */}
                            <div style={headerStyle}>
                              {/* ────────────────────────────────────────────────
                                  1) rc-tree‐indent (lege span)
                                  2) rc-tree‐switcher‐noop (lege switcher)
                                  3) rc-tree‐icon (lege icon) 
                                  ──────────────────────────────────────────────── */}
                              <span className="rc-tree-indent" />
                              <span className="rc-tree-switcher rc-tree-switcher-noop" />
                              <span className="rc-tree-icon" />

                              {/* De daadwerkelijke kolomtitels náást die lege placeholders */}
                              <div
                                style={{
                                  flex: 1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {headerJoined}
                              </div>
                            </div>

                            {/* Boom‐component: krijgt de resterende hoogte en scrollt alleen hier */}
                            <div style={{ height: treeContainerHeight, overflowY: 'auto' }}>
                              <Tree
                                treeData={treeData}
                                selectable={false}
                                defaultExpandAll={true}
                              />
                            </div>
                          </>
                        );
                      })()
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        <div>
                          <div className="spinner" style={{ margin: '0 auto' }}></div>
                          <div
                            style={{
                              textAlign: 'center',
                              marginTop: '10px',
                              fontSize: '1.1em',
                              fontWeight: 'bold',
                            }}
                          >
                            Loading tree data...
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === idx && layout.type === 'tabpage-layout' && layout.layoutConfig && (
                  <div 
                    style={{ 
                      display: 'flex', 
                      gap: '20px', 
                      width: '100%', 
                      height: layout.layoutConfig.height || '52vh', 
                      maxHeight: layout.layoutConfig.maxheight || '52vh', 
                      paddingTop: '10px' 
                    }}
                  >
                    {/* Column 1 */}
                    <div style={{ flex: 1 }}>
                      {(layout.layoutConfig.column1 || [])
                        .filter(fld => !fld.hide) // Alleen de velden waarvan hide niet true is tonen
                        .map((fld, i) => {
                        if (fld.type === 'image' || fld.type === 'thumb_image') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <div className="header_widget_1_value_text" style={{ fontWeight: 'bold' }}>
                                <img
                                  src={`data:image/png;base64,${formData[fld.field]}`}
                                  alt={fld.headerName}
                                  style={{ 
                                    height: fld.height || 'auto',
                                    width: fld.width || 'auto',
                                    minHeight: fld.minheight || 'auto',
                                    maxHeight: fld.maxheight || 'auto',
                                    minWidth: fld.minwidth || 'auto',
                                    maxWidth: fld.maxwidth || 'auto',
                                    borderRadius: '4px'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        } else if (fld.type === 'plaintext_memo') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <div
                                className="header_widget_1_value_text"
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '4px',
                                  overflow: 'auto'
                                }}
                              >
                                {formatValue(fld, formData[fld.field])}
                              </div>
                            </div>
                          );
                        } else if (fld.type === 'rtf_memo') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <RtfMemo 
                                rtfText={formData[fld.field] || ""}
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '0px',
                                  overflow: 'auto'
                                }}
                              />
                            </div>
                          );
                        } else if (fld.type === 'video') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              {/* Als headerName leeg is, kan je eventueel niets renderen, of een titel toevoegen */}
                              {fld.headerName && <div className="header_widget_1_key_text">{fld.headerName}</div>}
                              <div
                                className="header_widget_1_value_text"
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '0px',
                                  overflowY: 'hidden',
                                  overflowX: 'auto'
                                }}
                              >
                                <iframe
                                  width="100%"
                                  height="100%"
                                  src={convertYoutubeURLtoEmbed(getVideoUrl(fld, formData))}
                                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  title="Video"
                                />
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={i} style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                              <div className="header_widget_1_key_text" style={{ marginRight: '10px', width: '200px' }}>
                                {fld.headerName}:
                              </div>
                              <div className="header_widget_1_value_text" style={{ fontWeight: 'bold', flex: 1 }}>
                                {formatValue(fld, formData[fld.field])}
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                    {/* Column 2 */}
                    <div style={{ flex: 1 }}>
                      {(layout.layoutConfig.column2 || [])
                        .filter(fld => !fld.hide) // Ook hier filteren we op hide
                        .map((fld, i) => {
                        if (fld.type === 'image' || fld.type === 'thumb_image') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <div className="header_widget_1_value_text" style={{ fontWeight: 'bold' }}>
                                <img
                                  src={`data:image/png;base64,${formData[fld.field]}`}
                                  alt={fld.headerName}
                                  style={{ 
                                    height: fld.height || 'auto',
                                    width: fld.width || 'auto',
                                    minHeight: fld.minheight || 'auto',
                                    maxHeight: fld.maxheight || 'auto',
                                    minWidth: fld.minwidth || 'auto',
                                    maxWidth: fld.maxwidth || 'auto',
                                    borderRadius: '4px'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        } else if (fld.type === 'plaintext_memo') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <div
                                className="header_widget_1_value_text"
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '4px',
                                  overflow: 'auto'
                                }}
                              >
                                {formatValue(fld, formData[fld.field])}
                              </div>
                            </div>
                          );
                        } else if (fld.type === 'rtf_memo') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              <div className="header_widget_1_key_text">{fld.headerName}</div>
                              <RtfMemo 
                                rtfText={formData[fld.field] || ""}
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '0px',
                                  overflow: 'auto'
                                }}
                              />
                            </div>
                          );
                        } else if (fld.type === 'video') {
                          return (
                            <div key={i} style={{ marginTop: '10px' }}>
                              {/* Als headerName leeg is, kan je eventueel niets renderen, of een titel toevoegen */}
                              {fld.headerName && <div className="header_widget_1_key_text">{fld.headerName}</div>}
                              <div
                                className="header_widget_1_value_text"
                                style={{
                                  height: fld.height || 'auto',
                                  width: fld.width || 'auto',
                                  minHeight: fld.minheight || 'auto',
                                  maxHeight: fld.maxheight || 'auto',
                                  minWidth: fld.minwidth || 'auto',
                                  maxWidth: fld.maxwidth || 'auto',
                                  border: '1px solid #ccc',
                                  borderRadius: '4px',
                                  padding: '0px',
                                  overflowY: 'hidden',
                                  overflowX: 'auto'
                                }}
                              >
                                <iframe
                                  width="100%"
                                  height="100%"
                                  src={convertYoutubeURLtoEmbed(getVideoUrl(fld, formData))}
                                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                  allowFullScreen
                                  title="Video"
                                />
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={i} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                              <div className="header_widget_1_key_text" style={{ marginRight: '10px', width: '200px' }}>
                                {fld.headerName}
                              </div>
                              <div className="header_widget_1_value_text" style={{ fontWeight: 'bold', flex: 1 }}>
                                {formatValue(fld, formData[fld.field])}
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}
              </TabPanel>
            ))}
          </Tabs>
        </div>
      );
    }
    return null;
  };

  // Dynamische titel (formtitle)
  let formTitle = `Form`;
  if (formConfig.formtitle) {
    try {
      const data = formData;
      formTitle = eval(formConfig.formtitle);
    } catch (e) {
      console.error('Error evaluating formtitle:', e);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', color: 'gray' }}>
        {showBack && (
          <i
            className="fas fa-arrow-left arrow-hover"
            style={{ cursor: 'pointer', marginRight: '5px', fontSize: '20px', padding: '10px' }}
            onClick={onClose}
          ></i>
        )}
        <h2 className="form_header_text" style={{ color: '#333', margin: 0 }}>{formTitle}</h2>
      </div>
      <div style={{ display: 'flex', gap: '20px', justifyContent: 'space-between' }}>
        {widgetLeft && renderWidgetColumn(widgetLeft, 'header_widget_1')}
        {widgetRight && renderWidgetColumn(widgetRight, 'header_widget_2')}
      </div>
      {hasTabLayouts && renderTabs()}
    </div>
  );
}

export default FormPage;
